import React from "react";
import { Divider } from "primereact/divider";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { theme } from "styles/theme";
import daveyLogo from "assets/Davey_logotype_Yellow.svg";

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Header = styled.header`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 30px;
  margin-right: 20px;
`;

const AppTitle = styled.h1`
  margin: 0;
  font-size: ${theme.fontSizes.large};
`;

const BackButton = styled(Link)`
  background-color: transparent;
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.small};
  padding: 0.5rem 1rem;
  font-size: ${theme.fontSizes.small};
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: flex;
  align-items: center;

  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 2rem;
  background-color: ${theme.colors.grey};
  min-height: calc(100vh - 80px);
  overflow-y: auto;
`;

const Footer = styled.footer`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.lightGrey};
  padding: 1rem 2rem;
  text-align: center;
  font-size: ${theme.fontSizes.small};
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  font-size: 28px;
`;

const SubTitle = styled.p`
  color: ${theme.colors.darkGrey};
  font-size: 16px;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.secondary};
  margin: 1.5rem 0 1rem;
  font-size: 20px;
`;

const SubSectionTitle = styled.h3`
  color: ${theme.colors.secondary};
  margin: 1rem 0 0.5rem;
  font-size: 18px;
  font-weight: 500;
`;

const SectionText = styled.p`
  color: ${theme.colors.darkGrey};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ListItem = styled.li`
  color: ${theme.colors.darkGrey};
  margin-bottom: 0.5rem;
  line-height: 1.6;
`;

const ListContainer = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 2rem;
`;

const ContactLink = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const TermsConditions: React.FC = () => {
  return (
    <LayoutContainer>
      <Header>
        <HeaderLeft>
          <Logo src={daveyLogo} alt="Davey Logo" />
          <AppTitle>Pool Professional</AppTitle>
        </HeaderLeft>
        <BackButton to="/auth/signin">Back to Login</BackButton>
      </Header>

      <MainContent>
        <ContentWrapper>
          <Title>Privacy Policy</Title>
          <SubTitle>Last Updated: May 7, 2025</SubTitle>

          <SectionTitle>1. Privacy Policy</SectionTitle>
          <SectionText>
            Davey Water Products Pty Ltd (Davey) respects your privacy and is
            committed to protecting your personal information. This Privacy
            Policy applies to the Davey Pool Professional App and our other
            apps, including MyPool, ProMatic, and Lifeguard apps (collectively,
            the "Davey Apps"). If you have questions or need to contact us about
            your privacy, please email{" "}
            <ContactLink href="mailto:privacy@davey.com.au">
              privacy@davey.com.au
            </ContactLink>{" "}
            or visit our website at{" "}
            <ContactLink href="http://www.daveywater.com">
              www.daveywater.com
            </ContactLink>
            .
          </SectionText>
          <Divider />

          <SectionTitle>2. What is personal information?</SectionTitle>
          <SectionText>
            Personal information is information or an opinion about an
            identified or reasonably identifiable individual, whether or not the
            information or opinion is true and whether or not the information is
            recorded in a material form. We use the term personal information in
            this Privacy Policy to refer to personal information that is
            regulated by applicable data protection and privacy laws.
          </SectionText>
          <Divider />

          <SectionTitle>3. What Information We Collect</SectionTitle>
          <SubSectionTitle>
            When you use any Davey App, we may collect:
          </SubSectionTitle>
          <ListContainer>
            <ListItem>
              <strong>Identity and contact information:</strong> Name, email
              address, residential address, telephone numbers;
            </ListItem>
            <ListItem>
              <strong>User settings and profile data:</strong> Usernames,
              passwords, preference data, purchase history, device ID and
              related information; and
            </ListItem>
            <ListItem>
              <strong>System logs and other digital information:</strong> We
              collect text of written and oral communications gathered in the
              course of our interaction with you, including firmware updates,
              device history logs, where you interact on live-chat, social media
              and email systems.
            </ListItem>
          </ListContainer>
          <Divider />

          <SectionTitle>4. Purposes of collection</SectionTitle>
          <SectionText>
            We will not use or disclose personal information for any secondary
            purpose, unless that secondary purpose is related to the primary
            purpose for which we have collected that information, and you would
            reasonably expect the disclosure in the circumstances, or we obtain
            your consent, or we are required or permitted by law to do so.
          </SectionText>
          <SubSectionTitle>
            The purposes for which we may collect, hold, use and disclose your
            personal information include to:
          </SubSectionTitle>
          <ListContainer>
            <ListItem>register and manage your account;</ListItem>
            <ListItem>provide technical and maintenance support;</ListItem>
            <ListItem>
              communicate information to you about our activities, send
              notifications and/or updates;
            </ListItem>
            <ListItem>
              provide you with information or advertising relating to our
              activities (including targeted advertisements) or marketing
              communications we believe may be of interest to you (with
              consent);
            </ListItem>
            <ListItem>
              customise app experience and improve and/or enhance product
              performance and scheduling;
            </ListItem>
            <ListItem>
              undertake our internal administrative, research, planning and
              marketing purposes; and
            </ListItem>
            <ListItem>
              comply with legal obligations and protect our legal interests and
              rights (including our intellectual property rights).
            </ListItem>
          </ListContainer>
          <Divider />

          <SectionTitle>5. Legal Basis for Processing (EU Users)</SectionTitle>
          <SectionText>
            Under the GDPR, the lawful bases for our processing of your personal
            information under are as follows:
          </SectionText>
          <ListContainer>
            <ListItem>
              With your consent (including for marketing communications). You
              are able to remove your consent at any time. You can do this by
              contacting{" "}
              <ContactLink href="mailto:privacy@davey.com.au">
                privacy@davey.com.au
              </ContactLink>
              .
            </ListItem>
            <ListItem>
              To enable us to perform a contract with you related to the
              products and services you have asked us to provide.
            </ListItem>
            <ListItem>
              For legitimate interests, which are the purposes necessary for our
              own, or a third party's legitimate interests, as captured by the
              purposes set out at section 4 of this Privacy Policy.
            </ListItem>
          </ListContainer>
          <Divider />

          <SectionTitle>
            6. How We Store and Protect Your Information
          </SectionTitle>
          <SectionText>
            We take reasonable steps to ensure the security and integrity of the
            personal information we collect, store, use and disclose. Personal
            information is held securely, is subject to various security
            protections.
          </SectionText>
          <SectionText>
            We use encryption and access controls to safeguard your information
            from misuse, loss, or unauthorised access. Data no longer required
            is securely deleted or de-identified.
          </SectionText>
          <Divider />

          <SectionTitle>7. Disclosure of Your Information</SectionTitle>
          <SectionText>
            We do not sell your personal information. We may disclose it to:
          </SectionText>
          <ListContainer>
            <ListItem>our affiliates and related bodies corporate;</ListItem>
            <ListItem>pool service providers;</ListItem>
            <ListItem>cloud and IT service providers; and</ListItem>
            <ListItem>legal or government bodies (if required).</ListItem>
          </ListContainer>
          <Divider />

          <SectionTitle>8. Your rights to privacy under the GDPR</SectionTitle>
          <SectionText>
            Where the GDPR applies to personal information and/or individuals,
            some additional rights apply as follows:
          </SectionText>
          <ListContainer>
            <ListItem>
              Some individuals also have a right, in certain circumstances, to
              have the information held about them erased.
            </ListItem>
            <ListItem>
              You can also request that we restrict or suspend the processing of
              your personal information. If you do so, note that we will then be
              most likely unable to provide the services to you.
            </ListItem>
            <ListItem>
              In certain circumstances, individuals may also have a right to
              data portability, to withdraw their consent at any time, to object
              to data processing and to object to processing of personal
              information for marketing purposes.
            </ListItem>
          </ListContainer>
          <SectionText>
            If you would like to exercise these rights, you may do so by
            contacting us, at{" "}
            <ContactLink href="mailto:privacy@davey.com.au">
              privacy@davey.com.au
            </ContactLink>
            . We may need to verify your identity before processing your
            request.
          </SectionText>
          <SectionText>
            When you object to the processing of your personal information, we
            will promptly assess your objection and respond to you in a timely
            manner. Please note that there may be circumstances in which we are
            still able to process your personal information, for example when we
            need to do so to comply with an Australian law or other legal
            requirement.
          </SectionText>
          <Divider />

          <SectionTitle>9. Data retention, access and correction</SectionTitle>
          <SectionText>
            We retain your personal information only for as long as needed to
            fulfill the purposes outlined in section 4, or as required by
            applicable laws and regulations.
          </SectionText>
          <SectionText>
            We will take all reasonable steps to ensure any personal information
            we collect, use or disclose is up to date and accurate. If you
            believe personal information that we hold about you is not up to
            date or accurate, you may ask us to correct it.
          </SectionText>
          <SectionText>
            You may ask us to provide you with details of the personal
            information we hold about you, and copies of that information. We
            will respond to your request within 30 days of receipt of your
            request, or otherwise in accordance with Applicable Privacy
            Legislation.
          </SectionText>
          <SectionText>
            We will not charge you for accessing your personal information
            unless your request is manifestly unfounded or excessive. In such
            cases, we may charge a reasonable fee to cover our administrative
            costs.
          </SectionText>
          <Divider />

          <SectionTitle>10. Overseas disclosure</SectionTitle>
          <SectionText>
            Relevant to the GDPR, in order to provide our products and services
            to you, we may disclose the information which we process to
            countries outside the UK and/or the European Economic Area (EEA).
            Specifically, we may transfer your personal information to entities
            in Australia, and/or other countries. These transfers are subject to
            safeguards to protect your personal information.
          </SectionText>
          <Divider />

          <SectionTitle>11. Managing your privacy preferences</SectionTitle>
          <SectionText>
            You can manage your preferences directly within the App:
          </SectionText>
          <ListContainer>
            <ListItem>enable/disable notifications;</ListItem>
            <ListItem>update marketing and update consents; and</ListItem>
            <ListItem>delete your account.</ListItem>
          </ListContainer>
          <Divider />

          <SectionTitle>12. Complaints or Concerns</SectionTitle>
          <SectionText>
            If you consider a breach of the Privacy Act 1988 (Cth) has occurred,
            you may direct your query to our privacy officer using the details
            set out below and we will attempt to resolve your complaint.
          </SectionText>
          <SectionText>
            We would appreciate the chance to deal with your concerns before you
            approach the relevant authority so please contact us in the first
            instance. If you do not consider our response satisfactory, you may
            contact the Australian Privacy Commissioner at{" "}
            <ContactLink href="http://www.oaic.gov.au">
              www.oaic.gov.au
            </ContactLink>
            .
          </SectionText>
          <SectionText>
            If you are in the UK, you have the right to make a complaint at any
            time to the Information Commissioner's Office (ICO), the UK
            regulator for data protection issues (
            <ContactLink href="http://www.ico.org.uk">
              www.ico.org.uk
            </ContactLink>
            ).
          </SectionText>
          <SectionText>
            If you are in the EU, you have the right to lodge a complaint with
            your supervisory authority, whose contact details can be found here{" "}
            <ContactLink href="https://edpb.europa.eu/about-edpb/about-edpb/members_en">
              https://edpb.europa.eu/about-edpb/about-edpb/members_en
            </ContactLink>
            .
          </SectionText>
          <SectionText>
            If you have any questions about this policy and would like further
            information, please contact us at{" "}
            <ContactLink href="mailto:privacy@davey.com.au">
              privacy@davey.com.au
            </ContactLink>
            .
          </SectionText>
          <Divider />

          <SectionTitle>13. Updates to This Policy</SectionTitle>
          <SectionText>
            We may update this Privacy Policy periodically. The latest version
            will always be accessible via our apps and website.
          </SectionText>
        </ContentWrapper>
      </MainContent>

      <Footer>
        <p>© 2025 Davey Pool Professional</p>
      </Footer>
    </LayoutContainer>
  );
};

export default TermsConditions;
